import { useCallback, useEffect, useRef, useState } from "react";
import type { AnzeigePhase } from "@offsite/shared";
import { api } from "@/api/client";
import { useReadOnly, useStore, usePhase, useWorkshop } from "@/state/store";
import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { useOvernightStatus } from "@/hooks/useOvernightStatus";
import { useSitzungsZahlen } from "@/hooks/useSitzungsZahlen";
import { Timer } from "./primitives/Timer";
import { maxFreieFolie } from "@/pages/presenter-phase";

/**
 * Moderationsleiste des Stations-Ablaufs (Strategietag, Briefing Prompt-Teil):
 * Warten · Einführung · Thema · Arbeitsphase · Generierung · Vortrag ·
 * Ergebnisse · Abschluss — „Thema nutzen" startet die Arbeitsphase direkt,
 * einen eigenen Prompten-Schritt gibt es nicht mehr (17.09.). Jeder Schritt stempelt die
 * servergeführten Timer (anzeigePhase + phaseDauerSec, „Gruppe starten" die
 * 25 Minuten); die Seitenbildschirme lesen dieselben Stempel über /phase.
 *
 * Eigene Komponente neben ArModerationsleiste: die AR-Sitzung behält ihre
 * Sechser-Kette samt Exkurs — hier gibt es beides nicht (Briefing 8).
 */

function status(e: unknown): number | undefined {
  return typeof e === "object" && e !== null && "status" in e
    ? (e as { status?: number }).status
    : undefined;
}

interface Schritt {
  id: AnzeigePhase | "warten";
  label: string;
}

const SCHRITTE: Schritt[] = [
  { id: "warten", label: "Warten" },
  { id: "einfuehrung", label: "Einführung" },
  { id: "thema", label: "Thema" },
  { id: "arbeitsphase", label: "Arbeitsphase" },
  { id: "generierung", label: "Generierung" },
  { id: "vortrag", label: "Vortrag" },
  { id: "ergebnisse", label: "Ergebnisse" },
  { id: "abschluss", label: "Abschluss" },
];

function mmss(sekunden: number): string {
  const s = Math.max(0, Math.floor(sekunden));
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}

interface Props {
  slideIndex: number;
  onJumpSlide: (index: number) => void;
  onToggleForceSlides: (next: boolean) => void;
  /** Folien-Sperre aktiv? Nach dem Auto-Sprung auf Ergebnisse steht dann
   *  noch die V-Folie — der Weiter-Knopf muss „Ergebnisse zeigen" heißen. */
  forceSlides: boolean;
}

export function StationsLeiste({
  slideIndex,
  onJumpSlide,
  onToggleForceSlides,
  forceSlides,
}: Props) {
  const { state, dispatch } = useStore();
  const token = state.auth.token;
  const config = useOffsiteConfig();
  const ablauf = config.stationsAblauf!;
  // Strategietag setzt themaSlide immer — hier einmal festgenagelt.
  const themaFolie = config.themaSlide ?? ablauf.promptenSlide;
  const dauern = ablauf.phaseDauernSec;
  const phase = usePhase();
  const readOnly = useReadOnly();
  const phaseConfig = useWorkshop().phase;
  const {
    timerEndsAt,
    timerPausedRemainingSec,
    anzeigePhase,
    gruppeStartedAt,
  } = phaseConfig;

  const timerRunning = typeof timerEndsAt === "number" && timerEndsAt > Date.now();
  const timerPausiert =
    !timerRunning &&
    typeof timerPausedRemainingSec === "number" &&
    timerPausedRemainingSec > 0;

  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  // Höhe an das Folien-Layout melden — gleiches Muster wie die AR-Leiste.
  const leisteRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const el = leisteRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;
    const melden = () => {
      document.documentElement.style.setProperty(
        "--ar-leiste-h",
        `${Math.ceil(el.getBoundingClientRect().height)}px`,
      );
    };
    melden();
    const beobachter = new ResizeObserver(melden);
    beobachter.observe(el);
    return () => {
      beobachter.disconnect();
      document.documentElement.style.removeProperty("--ar-leiste-h");
    };
  }, []);

  const zahlen = useSitzungsZahlen(
    phase === "waiting" || phase === "einfuehrung" || phase === "working",
    token,
  );
  const overnight = useOvernightStatus(
    phase === "overnight" || phase === "reveal" || phase === "abschluss",
  );
  /** Bühne komplett gebaut — der Weiter-Knopf pulst, es springt aber nichts
   *  von selbst: die Moderation beendet erst ihren Vortrag (14.09.). */
  const alleFertig =
    phase === "overnight" &&
    overnight !== null &&
    overnight.total > 0 &&
    overnight.done >= overnight.total;

  /** Leinwand auf die Startfolie eines Schritts bringen und dort halten. */
  const leinwandAuf = useCallback(
    (id: Schritt["id"]) => {
      if (id === "ergebnisse") {
        onToggleForceSlides(false);
        return;
      }
      const index =
        id === "warten"
          ? config.waitingSlide
          : id === "einfuehrung"
            ? ablauf.einfuehrungSlides[0]
            : id === "thema"
              ? config.themaSlide
              : id === "prompten"
                ? ablauf.promptenSlide
                : id === "arbeitsphase"
                  ? config.workingSlide
                  : id === "generierung"
                    ? ablauf.generierungSlides[0]
                    : id === "vortrag"
                      ? ablauf.vortragSlides[0]
                      : ablauf.abschlussSlide;
      if (index === undefined) return;
      onToggleForceSlides(true);
      onJumpSlide(index);
    },
    [ablauf, config, onJumpSlide, onToggleForceSlides],
  );

  /** Aktiver Schritt — aus dem Server-Zustand, nicht aus der Folie. */
  const aktuelleId: Schritt["id"] = (() => {
    switch (phase) {
      case "waiting":
        return "warten";
      case "einfuehrung":
        return anzeigePhase === "thema" ? "thema" : "einfuehrung";
      case "working":
        return "arbeitsphase";
      case "overnight":
        return anzeigePhase === "vortrag" ? "vortrag" : "generierung";
      case "reveal":
        return "ergebnisse";
      case "abschluss":
        return "abschluss";
      default:
        return "warten";
    }
  })();
  const aktuellerIndex = SCHRITTE.findIndex((s) => s.id === aktuelleId);

  /**
   * Blättern aus dem Warten heraus startet die Gruppe (Wunsch 17.09.): Wer
   * mit den Pfeiltasten von der Warte-Folie in die Einführungsfolien springt,
   * soll unten nicht auf „Warten" hängen bleiben. Nur Phase + Uhren — die
   * Folie bleibt, wo die Moderation gerade hingeblättert hat.
   */
  const gruppeAutoGestartet = useRef(false);
  useEffect(() => {
    if (phase !== "waiting") {
      gruppeAutoGestartet.current = false;
      return;
    }
    const [vonSlide] = ablauf.einfuehrungSlides;
    // Bis einschließlich Themen-Folie: Wer schnell bis dorthin blättert,
    // bekommt erst den Gruppenstart, dann schaltet der Thema-Effekt weiter.
    const inEinfuehrung = slideIndex > vonSlide && slideIndex <= themaFolie;
    if (!inEinfuehrung || !token || readOnly || gruppeAutoGestartet.current) return;
    gruppeAutoGestartet.current = true;
    void (async () => {
      try {
        const p = await api.phase.set(token, "einfuehrung", undefined, {
          anzeigePhase: "einfuehrung",
          phaseDauerSec: dauern.einfuehrung,
          gruppeStart: true,
        });
        dispatch({ type: "phase/set", phase: p });
      } catch (e) {
        console.error(e);
        gruppeAutoGestartet.current = false;
      }
    })();
  }, [phase, slideIndex, token, readOnly, ablauf, themaFolie, dauern, dispatch]);

  /**
   * Gleiche Konsistenz einen Schritt weiter (Wunsch 17.09.): Erreicht die
   * Moderation blätternd die Themen-Folie, springt die Leiste auf „Thema"
   * (Phasen-Uhr Thema läuft) — statt unten auf „Einführung" zu stehen.
   */
  const themaAutoGeschaltet = useRef(false);
  useEffect(() => {
    const inEinfuehrung = phase === "einfuehrung" && anzeigePhase !== "thema";
    if (!inEinfuehrung) {
      themaAutoGeschaltet.current = false;
      return;
    }
    if (slideIndex !== themaFolie || !token || readOnly || themaAutoGeschaltet.current) {
      return;
    }
    themaAutoGeschaltet.current = true;
    void (async () => {
      try {
        const p = await api.phase.set(token, "einfuehrung", undefined, {
          anzeigePhase: "thema",
          phaseDauerSec: dauern.thema,
        });
        dispatch({ type: "phase/set", phase: p });
      } catch (e) {
        console.error(e);
        themaAutoGeschaltet.current = false;
      }
    })();
  }, [phase, anzeigePhase, slideIndex, token, readOnly, themaFolie, dauern, dispatch]);

  /**
   * Einen Schritt schalten. Kein Auto-Advance — die Moderation entscheidet
   * (Briefing 2); nur der Sprung Generierung → Ergebnisse passiert im Backend
   * von selbst, und selbst dann bleibt die V-Folie stehen, bis hier geklickt
   * wird (presenter-phase.ts, revealManuell).
   */
  const schalte = useCallback(
    async (id: Schritt["id"], erzwingen = false) => {
      // Klick auf den bereits aktiven Schritt springt nur mit der Leinwand
      // dorthin — er stempelt weder Timer noch Phase neu. Sonst setzte ein
      // Doppelklick auf „Arbeitsphase" allen Handys die 8:00 zurück
      // (Review-Fund 14.09.).
      if (id === aktuelleId && !erzwingen) {
        leinwandAuf(id);
        return;
      }
      setBusy(true);
      setFehler(null);
      try {
        if (id === "warten") {
          // Zurück auf Warten heißt hier „Leinwand parken", nicht „neue
          // Gruppe" — schon Angemeldete bleiben im Zähler. Neue Runden
          // beginnt der Gruppenwechsel in der Sidebar.
          const p = await api.phase.set(token, "waiting", undefined, {
            rundeBehalten: true,
          });
          dispatch({ type: "phase/set", phase: p });
        } else if (id === "einfuehrung" || id === "thema") {
          const p = await api.phase.set(token, "einfuehrung", undefined, {
            anzeigePhase: id,
            phaseDauerSec: dauern[id],
            // „Gruppe starten": der Schritt aus dem Warten heraus stempelt die
            // 25 Minuten IMMER frisch — nach „Warten" mit rundeBehalten liegt
            // sonst noch die Uhr der letzten Gruppe herum und startet mit
            // Überzug (Fund Fullrun 17.09.). Zurück von Thema auf Einführung
            // stempelt weiterhin nicht neu (aktuelleId ist dann nicht warten).
            gruppeStart:
              id === "einfuehrung" &&
              (aktuelleId === "warten" || gruppeStartedAt === undefined),
          });
          dispatch({ type: "phase/set", phase: p });
        } else if (id === "arbeitsphase") {
          // Servergeführte Phasen-Uhr UND der bestehende große Timer — die
          // Handys und die Leinwand-Mitte hängen am timerEndsAt.
          const p = await api.phase.set(token, "working", dauern.arbeitsphase, {
            anzeigePhase: "arbeitsphase",
            phaseDauerSec: dauern.arbeitsphase,
          });
          dispatch({ type: "phase/set", phase: p });
        } else if (id === "generierung") {
          // 409 = läuft bereits (Doppelklick, zweiter Bildschirm) — kein Fehler.
          try {
            await api.overnight.trigger(token);
          } catch (e) {
            if (status(e) !== 409) throw e;
          }
          const p = await api.phase.set(token, "overnight", undefined, {
            anzeigePhase: "generierung",
            phaseDauerSec: dauern.generierung,
          });
          dispatch({ type: "phase/set", phase: p });
        } else if (id === "vortrag") {
          // Kernphase bleibt overnight — die KI baut im Hintergrund weiter,
          // vorn läuft der Vortrag mit eigener Uhr und Auto-Flow.
          const p = await api.phase.set(token, "overnight", undefined, {
            anzeigePhase: "vortrag",
            phaseDauerSec: dauern.vortrag,
          });
          dispatch({ type: "phase/set", phase: p });
        } else if (id === "ergebnisse") {
          const p = await api.phase.set(token, "reveal", undefined, {
            anzeigePhase: "ergebnisse",
            phaseDauerSec: dauern.ergebnisse,
          });
          dispatch({ type: "phase/set", phase: p });
        } else {
          const p = await api.phase.set(token, "abschluss", undefined, {
            anzeigePhase: "abschluss",
            phaseDauerSec: dauern.abschluss,
          });
          dispatch({ type: "phase/set", phase: p });
        }
        leinwandAuf(id);
      } catch (e) {
        console.error(e);
        setFehler(`„${SCHRITTE.find((s) => s.id === id)?.label}“ fehlgeschlagen.`);
      } finally {
        setBusy(false);
      }
    },
    [token, dispatch, leinwandAuf, dauern, gruppeStartedAt, aktuelleId],
  );

  const timerAktion = useCallback(
    async (aktion: "pause" | "resume" | "plus" | "stop") => {
      setBusy(true);
      setFehler(null);
      try {
        const r =
          aktion === "pause"
            ? await api.admin.pauseTimer(token)
            : aktion === "resume"
              ? await api.admin.resumeTimer(token)
              : aktion === "plus"
                ? await api.admin.setTimer(token, 120, "extend")
                : await api.admin.resetTimer(token);
        dispatch({ type: "phase/set", phase: r.phase });
      } catch (e) {
        console.error(e);
        setFehler("Timer-Änderung fehlgeschlagen.");
      } finally {
        setBusy(false);
      }
    },
    [token, dispatch],
  );


  /**
   * Gruppenwechsel direkt aus der Leiste: archiviert Prompts und Ergebnisse
   * der Runde und stellt zurück auf Warten — der Abschluss-Schritt endet
   * damit, ohne den Umweg über die Sidebar.
   */
  const naechsteGruppe = useCallback(async () => {
    if (!window.confirm("Nächste Gruppe? Prompts und Ergebnisse dieser Runde werden archiviert.")) return;
    setBusy(true);
    setFehler(null);
    try {
      await api.admin.resetGroup(token, phaseConfig.gruppeLabel);
      const p = await api.phase.set(token, "waiting");
      dispatch({ type: "phase/set", phase: p });
      leinwandAuf("warten");
    } catch (e) {
      console.error(e);
      setFehler("Gruppenwechsel fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }, [token, dispatch, leinwandAuf, phaseConfig.gruppeLabel]);

  /**
   * Nach dem Auto-Sprung (Bühne fertig) steht die V-Folie noch — der primäre
   * Knopf muss dann die Ergebnisse auf die Leinwand bringen, nicht schon zum
   * Abschluss springen (Review-Fund 14.09.). Nach dem Abschluss ist der
   * nächste Handgriff der Gruppenwechsel.
   */
  const weiter = (():
    | { label: string; run: () => void }
    | null => {
    if (aktuelleId === "ergebnisse" && forceSlides) {
      return {
        label: "Ergebnisse zeigen",
        run: () => leinwandAuf("ergebnisse"),
      };
    }
    if (aktuelleId === "abschluss") {
      return { label: "Nächste Gruppe", run: () => void naechsteGruppe() };
    }
    // Redeteil (Warten/Einführung, vor der Themen-Folie): „Weiter" blättert
    // die Folie — die Auto-Effekte oben schalten Gruppe und Thema passend
    // mit. Vorher sprang der Knopf auf die Schritt-Startfolie zurück und
    // „bewegte" sich scheinbar nicht (Fund 17.09.).
    if (
      (aktuelleId === "warten" || aktuelleId === "einfuehrung") &&
      slideIndex < themaFolie
    ) {
      const naechste = slideIndex + 1;
      return naechste < themaFolie
        ? { label: "Nächste Folie", run: () => onJumpSlide(naechste) }
        : { label: "Thema", run: () => void schalte("thema") };
    }
    const next =
      aktuellerIndex >= 0 && aktuellerIndex < SCHRITTE.length - 1
        ? SCHRITTE[aktuellerIndex + 1]
        : null;
    return next
      ? { label: next.label, run: () => void schalte(next.id) }
      : null;
  })();
  // Kein Zurück aus/nach der Generierung: der Generierung-Schritt stößt den
  // Build an — aus „Ergebnisse" zurück hieße alle Prototypen neu bauen und
  // die gezeigten überschreiben (Review-Fund 14.09.).
  const zurueck = ((): { label: string; run: () => void } | null => {
    // Redeteil: Zurück blättert die Folie, statt zum Schritt-Anfang zu
    // springen — spiegelbildlich zum Weiter-Knopf.
    if (
      (aktuelleId === "warten" || aktuelleId === "einfuehrung") &&
      slideIndex > 0 &&
      slideIndex <= themaFolie
    ) {
      return { label: "Folie zurück", run: () => onJumpSlide(slideIndex - 1) };
    }
    if (
      aktuellerIndex > 0 &&
      !["generierung", "vortrag", "ergebnisse", "abschluss"].includes(aktuelleId)
    ) {
      const s = SCHRITTE[aktuellerIndex - 1];
      return { label: s.label, run: () => void schalte(s.id) };
    }
    return null;
  })();

  /** Lage-Zeile: die Zahl, auf die es im Schritt ankommt. */
  const lage = (() => {
    if (phase === "overnight") {
      // Statusanzeige, während die Moderation auf den V-Folien erzählt —
      // aus dem Augenwinkel lesbar, ob die Bühne durch ist.
      if (!overnight) return "Generierung: Fortschritt wird geladen…";
      const { done, total } = overnight;
      return total > 0 && done >= total
        ? `Generierung: alle ${done} Prototypen fertig ✓`
        : `Generierung: ${done} von ${total || "…"} fertig`;
    }
    if (phase === "reveal" && overnight && overnight.done > 0) {
      return `${overnight.done} Prototypen auf der Leinwand`;
    }
    if (!zahlen) return null;
    return phase === "working"
      ? `${zahlen.eingereicht} von ${zahlen.angemeldet} eingereicht`
      : `Bühne ${zahlen.buehne}/${config.buehnenPlaetze ?? 6} · ${zahlen.publikum} im Hintergrund`;
  })();


  // Freies Blättern endet am Horizont des erreichten Schritts — gleiche
  // Regel wie Pfeiltasten/Rand-Pfeile in der PresenterPage (Fund 17.09.).
  const maxFolie = maxFreieFolie(phase, anzeigePhase, {
    themaSlide: themaFolie,
    workingSlide: config.workingSlide ?? 0,
    vortragSlides: ablauf.vortragSlides,
    abschlussSlide: ablauf.abschlussSlide,
    deckLaenge: config.deck.length,
  });

  if (readOnly) return null;

  return (
    <div className="ar-leiste stations-leiste" data-testid="stations-leiste" ref={leisteRef}>
      <div className="ar-leiste-schritte" role="group" aria-label="Ablauf der Station">
        {SCHRITTE.map((schritt, idx) => (
          <button
            key={schritt.id}
            type="button"
            className={`ar-leiste-schritt${idx === aktuellerIndex ? " is-aktiv" : ""}${
              idx < aktuellerIndex ? " is-erledigt" : ""
            }`}
            data-testid={`stations-leiste-${schritt.id}`}
            onClick={() => void schalte(schritt.id)}
            disabled={busy}
            title={`Direkt zu: ${schritt.label}`}
          >
            <span className="ar-leiste-num">{idx + 1}</span>
            <span className="ar-leiste-label">{schritt.label}</span>
          </button>
        ))}
      </div>

      <div className="ar-leiste-mitte">
        {/* Folien-Steuerung dauerhaft in der Leiste: zarte Pfeile + Position
            (Wunsch 14.09.) — zusätzlich zu Tastatur/Klicker/Rand-Pfeilen. */}
        <span className="ar-leiste-folien" data-testid="leiste-folien">
          <button
            type="button"
            className="ar-leiste-mini"
            aria-label="Vorherige Folie"
            onClick={() => onJumpSlide(Math.max(0, slideIndex - 1))}
            disabled={slideIndex === 0}
          >
            ‹
          </button>
          <span className="ar-leiste-folien-stand">
            Folie {slideIndex + 1}/{config.deck.length}
          </span>
          <button
            type="button"
            className="ar-leiste-mini"
            aria-label="Nächste Folie"
            onClick={() =>
              onJumpSlide(Math.min(maxFolie, slideIndex + 1))
            }
            disabled={slideIndex >= maxFolie}
          >
            ›
          </button>
        </span>
        <span className="ar-leiste-zaehler">
          Schritt {aktuellerIndex + 1} von {SCHRITTE.length}
        </span>
        {/* KI-Baufortschritt dauerhaft klein, sobald die Generierung lief —
            durch Vortrag, Ergebnisse und Abschluss hindurch. */}
        <span
          className={`ar-leiste-ki${overnight && overnight.total > 0 && overnight.done >= overnight.total ? " is-fertig" : ""}`}
          data-testid="leiste-ki"
          title="Baufortschritt der KI (Bühnen-Prototypen)"
          style={{
            visibility:
              overnight && overnight.total > 0 ? "visible" : "hidden",
          }}
        >
          KI {overnight?.done ?? 0}/{overnight?.total ?? 0}
          {overnight && overnight.total > 0 && overnight.done >= overnight.total
            ? " ✓"
            : "…"}
        </span>
        {lage && (
          <span className="ar-leiste-lage" data-testid="stations-leiste-lage">
            {lage}
          </span>
        )}
        <span
          className={`ar-leiste-timer${timerPausiert ? " is-pausiert" : ""}`}
          style={{
            visibility: timerRunning || timerPausiert ? "visible" : "hidden",
          }}
        >
          {timerPausiert ? (
            <>{mmss(timerPausedRemainingSec ?? 0)} pausiert</>
          ) : timerRunning && typeof timerEndsAt === "number" ? (
            <Timer deadline={timerEndsAt} size="inline" />
          ) : (
            "0:00"
          )}
        </span>
      </div>

      <div className="ar-leiste-aktionen">
        {(timerRunning || timerPausiert) && (
          <>
            <button
              type="button"
              className="ar-leiste-klein"
              onClick={() => void timerAktion(timerPausiert ? "resume" : "pause")}
              disabled={busy}
              data-testid="stations-leiste-pause"
            >
              {timerPausiert ? "Weiter" : "Pause"}
            </button>
            {/* +2 nur bei laufender Uhr: im Pausenzustand würde extend die
                eingefrorene Restzeit verwerfen (Review-Fund 14.09.). */}
            {timerRunning && (
              <button
                type="button"
                className="ar-leiste-klein"
                onClick={() => void timerAktion("plus")}
                disabled={busy}
                data-testid="stations-leiste-plus"
                title="Zwei Minuten dazu — verlängert Phase und Gruppe"
              >
                +2
              </button>
            )}
          </>
        )}

        {zurueck && (
          <button
            type="button"
            className="ar-leiste-klein"
            onClick={zurueck.run}
            disabled={busy}
            data-testid="stations-leiste-zurueck"
            title={`Zurück: ${zurueck.label}`}
          >
            ← Zurück
          </button>
        )}

        {weiter && (
          <button
            type="button"
            className={`ar-leiste-weiter${alleFertig ? " is-bereit" : ""}`}
            onClick={weiter.run}
            disabled={busy}
            data-testid="stations-leiste-weiter"
          >
            {busy
              ? "…"
              : aktuelleId === "warten"
                ? "Gruppe starten →"
                : `Weiter: ${weiter.label} →`}
          </button>
        )}
      </div>

      {fehler && (
        <p className="ar-leiste-fehler" role="alert">
          {fehler}
        </p>
      )}
    </div>
  );
}
